import { useState, FunctionComponent } from 'react';
import { Table, TableRow, TableCell, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { Form } from '@lmig/lmds-react-form';
import { FieldGroup } from '@lmig/lmds-react-field-group';
import { AlphanumericInput } from '@lmig/lmds-react-alphanumeric-input';
import { Button } from '@lmig/lmds-react-button';
import { Link } from '@lmig/lmds-react-link';
import { Heading } from '@lmig/lmds-react-typography';
import { formatName, formatPhone } from 'Utilities/StringFormatter';
import './FindAnAdjuster.scss';
import departments from 'Data/Claims/departments.json';
import jobcodes from 'Data/Claims/job_code_exclusions.json';
import exclusions from 'Data/Claims/job_dept_exclusions.json';
import grade_exclusions from 'Data/Claims/job_grade_exclusions.json';

const FindAnAdjuster: FunctionComponent = () => {
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastName, setLastName] = useState('');
  const [items, setItems] = useState([] as any[]);
  const [itemCount, setItemCount] = useState(0);

  const getManager = async (adjuster: any) => {
    if (adjuster.manager_employee_number || adjuster.manager_pin)
      await fetch(
        `/api/sitecore/ClaimAsync/GetAdjusterByN?input=${adjuster.manager_employee_number ?? adjuster.manager_pin}`,
        {
          method: 'GET',
        },
      )
        .then((res) => res.json())
        .then(
          (result: any) => {
            if (grade_exclusions.includes(result.job_code)) {
              adjuster.manager = {};
              adjuster.manager.error = ' '; //do not display a manager with a job grade above 19
            } else adjuster.manager = result;

            setItems((items) => [...items]);
          },
          () => {
            adjuster.manager = {};
            adjuster.manager.error = 'Manager not found';
            setItems((items) => [...items]);
          },
        );
  };

  const getAdjusters = async () => {
    setItems([]);

    if (lastName.length < 3) {
      setErrorMessage('Name must include 3 or more alphabetic characters');
      return;
    }
    setErrorMessage('');
    if (isSending) return;

    setIsSending(true);

    await fetch(`/api/sitecore/ClaimAsync/GetAdjusterByLastName?input=${lastName}`, {
      method: 'GET',
    })
      .then((res) => res.json())
      .then(
        (result) => {
          /*there is probably some redundant filtering happening here, but it may not be worth 
           sorting out bc of the elaborate inferences and mappings required 
           to stastify the business requirement of only returning adjusters that agents 
           are meant to get in contact with*/

          if (result.length > 0) {
            result.sort((a: any, b: any) =>
              `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`),
            ); //sort results first by last name then by first name

            result = result.filter((val: any) => val.first_name && val.last_name && val.job_code && val.dept_code); //filter out entries missing required fields

            result = result.filter((val: any) => !!getDepartmentName(val.dept_code)); //filter by department code

            result = result.filter((val: any) => getDepartmentName(val.dept_code) !== 'Shared Services'); //filter out all Shared Services

            result = result.filter((val: any) => !jobcodes.includes(val.job_code)); //filter by job code

            result = result.filter((val: any) => !grade_exclusions.includes(val.job_code)); //filter out job grades above 19

            result = result.filter(
              (val: any) =>
                !(exclusions as any)[getDepartmentName(val.dept_code)] ||
                ((exclusions as any)[getDepartmentName(val.dept_code)] != null &&
                  !(exclusions as any)[getDepartmentName(val.dept_code)].includes(val.job_code)),
            ); //filter out particular department and job code combinations

            result = result.filter((val: any) => val.assignment_status_ind === '1'); //filter by active/inactive status

            setItemCount(result.length);
            setItems(result);
            for (var i = 0; i < result.length; i++) {
              getManager(result[i]);
            }
          }

          if (result.length === 0) setErrorMessage('No results found');

          setIsSending(false);
        },
        () => {
          setErrorMessage('Something went wrong.');
          setIsSending(false);
        },
      );
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      getAdjusters();
    }
  };

  const getDepartmentName = (code: string): string => (departments as any)[`${parseInt(code)}`]; //map 05423 -> 5423, 00122 -> 122 etc. then lookup name from json

  return (
    <>
      <Form style={{ margin: '1rem 0 2rem 0' }}>
        <FieldGroup messages={errorMessage && errorMessage.length > 0 ? [{ text: errorMessage, type: 'error' }] : []}>
          <div className="inline-form">
            <AlphanumericInput
              onKeyDown={(e) => handleKeyDown(e)}
              labelVisual="Adjuster's last name"
              type="text"
              style={{ width: '320px', marginRight: '1rem' }}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setErrorMessage('');
              }}
            />
            <Button onClick={() => getAdjusters()} size="medium" variant="primary" className="no-button-margin">
              Search
            </Button>
          </div>
        </FieldGroup>
      </Form>
      {isSending ? (
        <div style={{ width: '453px', display: 'flex', justifyContent: 'center' }}>
          <img
            src="/-/media/Project/Safeco/shared/Rewards/spinner.gif"
            alt="loading"
            className="loading-spinner"
            style={{ margin: '3rem' }}
          />
        </div>
      ) : (
        <></>
      )}
      {items.length > 0 ? (
        <>
          <Heading align="left" type="h4-light">{`${itemCount} result${itemCount === 1 ? '' : 's'} found`}</Heading>
          <Table style={{ marginBottom: '3.5rem' }} rowStyle="zebra">
            <TableHeader className="scrollable-table-head">
              <TableRow>
                <TableCell type="colHead">Adjuster</TableCell>
                <TableCell type="colHead">Adjuster's Manager</TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="scrollable-table-body">
              {items.map((item, index) => (
                <TableRow style={{ textTransform: 'capitalize' }} key={index}>
                  <TableCell>
                    <div>{formatName(item.first_name, item.middle_name, item.last_name)}</div>
                    <div>{getDepartmentName(item.dept_code)}</div>
                    <div>{formatPhone(item.work_phone ?? item.work_mobile_number)}</div>
                    <Link
                      variant="navigational"
                      href={`mailto:${item.employee_email_address ?? 'HRPORTAL2@LIBERTYMUTUAL.COM'}`}
                    >
                      {item.employee_email_address ?? 'HRPORTAL2@LIBERTYMUTUAL.COM'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {item.manager === undefined ? (
                      <img
                        src="/-/media/Project/Safeco/shared/Rewards/spinner.gif"
                        alt="loading"
                        className="loading-spinner"
                        style={{ marginLeft: '1rem', marginTop: '1.5rem' }}
                      />
                    ) : item.manager.error ? (
                      <div>{item.manager.error}</div>
                    ) : (
                      <>
                        <div>
                          {formatName(item.manager.first_name, item.manager.middle_name, item.manager.last_name)}
                        </div>
                        <div>{getDepartmentName(item.manager.dept_code)}</div>
                        <div>{formatPhone(item.manager.work_phone ?? item.manager.work_mobile_number)}</div>
                        <Link
                          variant="navigational"
                          href={`mailto:${item.manager.employee_email_address ?? 'HRPORTAL2@LIBERTYMUTUAL.COM'}`}
                        >
                          {item.manager.employee_email_address ?? 'HRPORTAL2@LIBERTYMUTUAL.COM'}
                        </Link>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default FindAnAdjuster;
